import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Panel } from "@/components/home/panel";
import { BadgeShelf, ScoreBreakdown, type Badge, type ScoreLine } from "@/components/Leaderboard";
import { TierProgress, type TierStep } from "@/components/tiers";

interface Standing {
  rank: number;
  of: number;
  score: number;
  components: ScoreLine[];
  behind_next: number | null;
  next_rank: number | null;
  per_active_day: number;
  days_active: number;
  listings: number;
  streak: number;
  fx_complete: boolean;
  tier: string;
  next_tier: string | null;
  to_next_tier: number | null;
}
interface Board {
  period_label: string;
  goal_daily: number;
  me: Standing | null;
  my_badges: Badge[];
  tiers: TierStep[];
}

/** The viewer's own score and badge shelf.
 *
 *  Mounted on Profile only — it reports on *you*, and the endpoint is
 *  self-scoped, so it must not be reused to render someone else's row.
 */
export function ScoreCard() {
  const [data, setData] = useState<Board | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let alive = true;
    api<Board>("/api/leaderboard")
      .then((r) => alive && setData(r))
      // 403 = the board is switched off for this role. Say nothing.
      .catch(() => alive && setHidden(true));
    return () => {
      alive = false;
    };
  }, []);

  if (hidden || !data) return null;
  if (!data.me) return null;

  return (
    <div className="space-y-3">
      <ScoreBreakdown
        me={data.me}
        periodLabel={data.period_label}
        goalDaily={data.goal_daily}
      />
      <Panel title="Your tier">
        <TierProgress
          tier={data.me.tier}
          score={data.me.score}
          steps={data.tiers}
          toNext={data.me.to_next_tier}
          nextTier={data.me.next_tier}
        />
      </Panel>

      {data.my_badges.length > 0 && (
        <Panel
          title={`Badges · ${new Set(data.my_badges.map((b) => b.kind)).size}`}
          right={
            <Link to="/leaderboard" className="text-accent-300 hover:underline">
              board
            </Link>
          }
          flush
        >
          <BadgeShelf badges={data.my_badges} />
        </Panel>
      )}
    </div>
  );
}
